import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';

const InactivityWarning = ({ 
  onLogout, 
  timeoutMinutes = 30
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const showWarningRef = useRef(false); // Use ref to track warning state for event handlers

  // Calculate warning time based on timeout
  const getWarningSeconds = () => {
    if (timeoutMinutes <= 1) return 15;
    if (timeoutMinutes <= 5) return 30;
    return 60;
  };

  const warningSeconds = getWarningSeconds();
  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningSeconds * 1000;
  const idleTimeMs = Math.max(1000, timeoutMs - warningMs); // At least 1 second of idle time

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  // Start the warning countdown
  const startWarning = useCallback(() => {
    showWarningRef.current = true;
    setShowWarning(true);
    setSecondsRemaining(warningSeconds);
    
    // Start countdown
    countdownRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningSeconds]);

  // Reset all timers and hide warning
  const resetTimers = useCallback(() => {
    clearAllTimers();
    showWarningRef.current = false;
    setShowWarning(false);
    setSecondsRemaining(0);

    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      startWarning();
    }, idleTimeMs);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      clearAllTimers();
      showWarningRef.current = false;
      setShowWarning(false);
      onLogout('inactivity');
    }, timeoutMs);
  }, [clearAllTimers, startWarning, idleTimeMs, timeoutMs, onLogout]);

  // Handle stay logged in
  const handleStayLoggedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Handle logout now
  const handleLogoutNow = useCallback(() => {
    clearAllTimers();
    showWarningRef.current = false;
    setShowWarning(false);
    onLogout('inactivity');
  }, [clearAllTimers, onLogout]);

  // Setup activity tracking and initial timers
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    let throttleTimer = null;
    
    const handleActivity = () => {
      // Don't reset if warning is showing - user must click button
      if (showWarningRef.current) return;
      if (throttleTimer) return;
      
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        // Double-check warning state before resetting
        if (!showWarningRef.current) {
          resetTimers();
        }
      }, 1000);
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetTimers();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [resetTimers, clearAllTimers]);

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl p-6 max-w-md mx-4 animate-in fade-in zoom-in duration-300">
        {/* Warning Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-white text-center mb-2">
          Session Timeout Warning
        </h2>

        {/* Message */}
        <p className="text-slate-300 text-center mb-4">
          You've been inactive for a while. For security and to reduce API usage, 
          your session will automatically end soon.
        </p>

        {/* Countdown */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Clock className="w-5 h-5 text-amber-400" />
          <span className="text-2xl font-mono font-bold text-amber-400">
            {formatTime(secondsRemaining)}
          </span>
          <span className="text-slate-400 text-sm">until logout</span>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-slate-700 rounded-full mb-6 overflow-hidden">
          <div 
            className="h-full bg-amber-400 transition-all duration-1000"
            style={{ width: `${(secondsRemaining / warningSeconds) * 100}%` }}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleLogoutNow}
            className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg font-medium transition-colors"
            data-testid="logout-now-btn"
          >
            Log Out Now
          </button>
          <button
            onClick={handleStayLoggedIn}
            className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors"
            data-testid="stay-logged-in-btn"
          >
            Stay Logged In
          </button>
        </div>

        {/* Note */}
        <p className="text-xs text-slate-500 text-center mt-4">
          This helps reduce unnecessary API calls from idle sessions
        </p>
      </div>
    </div>
  );
};

export default InactivityWarning;
